import { Component } from '@theme/component';

/**
 * Coordinates the native slideshow with drawer visibility, focus and reduced motion.
 * @extends {Component}
 */
class CartBenefitsComponent extends Component {
  #controller;
  #observer;
  #visible = false;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {import('./slideshow.js').Slideshow | null | undefined} */
  #managedSlideshow;

  get #slideshow() {
    return /** @type {import('./slideshow.js').Slideshow | null} */ (this.querySelector('slideshow-component'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#managedSlideshow?.pause();
    this.#managedSlideshow = undefined;
    this.#visible = false;
  }

  async #setup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#managedSlideshow?.pause();
    this.#managedSlideshow = undefined;
    this.#visible = false;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    await customElements.whenDefined('slideshow-component');
    if (signal.aborted || !this.isConnected) return;

    this.#managedSlideshow = this.#slideshow;
    this.#managedSlideshow?.setAttribute('aria-live', 'off');
    this.#observer = new IntersectionObserver(([entry]) => {
      if (signal.aborted) return;
      this.#visible = Boolean(entry?.isIntersecting);
      this.#sync();
    });
    this.#observer.observe(this);
    this.addEventListener('mouseenter', this.#sync, { signal });
    this.addEventListener('mouseleave', this.#sync, { signal });
    this.addEventListener('focusin', this.#sync, { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!signal.aborted) this.#sync();
      });
    }, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });
    this.#motion.addEventListener('change', this.#sync, { signal });
    this.#sync();
  }

  #sync = () => {
    const slideshow = this.#managedSlideshow;
    if (!slideshow) return;
    if (!this.isConnected) {
      slideshow.pause();
      return;
    }

    const motionReduced = this.#motion.matches;
    this.toggleAttribute('data-reduced-motion', motionReduced);
    const canPlay = !motionReduced && !Shopify.designMode && !document.hidden &&
      this.#visible && this.closest('dialog')?.open && !this.matches(':hover') && !this.querySelector(':focus-visible');

    if (canPlay) slideshow.play();
    else slideshow.pause();
  };
}

if (!customElements.get('cart-benefits-component')) {
  customElements.define('cart-benefits-component', CartBenefitsComponent);
}
